import React from 'react';
import DetailModal from 'components/modals/DetailModal';
import { DetailCardType } from 'types/DetailPageTypes';
import { HeaderButtonsConfig } from 'types/ButtonTypes';
import SupplierForm from '../../views/contacts/SupplierForm';

// Function to determine the client field link url based on its entity type
const determineLinkUrl = (client: any) => {
    if (client && client['entity_type'] && client['id']) {
        const baseUrl = client['entity_type'] === 'organization' ? '/contacts/organization' : client['entity_type'] === 'person' ? '/contacts/person' : '/contacts';
        return `${baseUrl}/${client['id']}`;
    }
    return null;
}

// Rename the name of the function:
const PurchaseModal: React.FC<{ viewKey?: string, itemId?: number }> = ({ viewKey, itemId }) => {

    // Specify the parameters of this detail modal:
    const apiObject = 'purchase';
    const object = 'purchase';
    const showStatusBar = false;
    const showSummary = true;

    // Define the header buttons of the modal:
    const headerButtons: HeaderButtonsConfig = {
        leftButtons: [
            {   
                type: 'dropdown',
                label: 'button.more_button_label',
                buttons: [
                    {
                        label: 'button.reopen_purchase_button_label',
                        action: 'reopen_purchase',
                        condition: [{ field: 'status', values: ['delivered'] }],
                        restrictedRight: ['only_view'],
                    },
                    {
                        label: 'button.delete_purchase_button_label',
                        action: 'delete',
                        restrictedRight: ['only_view'],
                        allowedRight: ['can_delete'],
                    },
                ]
            },
        ],
        rightButtons: [
            {   
                type: 'primary',
                label: 'button.send_purchase_button_label',
                action: 'send_purchase',
                restrictedRight: ['only_view'],
                customClass: 'purchase_sent',
                condition: [{ field: 'status', values: ['draft'] }]
            },
            {   
                type: 'secondary',
                label: 'button.confirm_purchase_button_label',
                action: 'confirm_purchase',
                restrictedRight: ['only_view'],
                customClass: 'purchase_confirmed',
                condition: [
                    { field: 'status', values: ['open'] },
                    { field: 'is_checked', values: [false] },
                ]
            },
            {   
                type: 'primary',
                label: 'button.purchase_delivered_button_label',
                action: 'purchase_delivered',
                restrictedRight: ['only_view'],
                customClass: 'purchase_delivered',
                condition: [{ field: 'status', values: ['open', 'confirmed', 'expired'] }]
            },
        ]
    }

    // Define the sections of the modal
    const sections: DetailCardType[] = [
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'purchase.general.supplier_card_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'supplier',
                            label: 'purchase.general.supplier_label',
                            placeholder: 'purchase.general.supplier_placeholder',
                            objectName: 'supplier.general.object_name.singular',
                            query: {
                                // Get all contacts which are suppliers
                                endpoint: 'get_contact_search',
                                params: {
                                    contact_type: ['supplier'] 
                                }
                            },
                            postEndpoint: 'post_contact',
                            viewModeLinkUrl: { function: determineLinkUrl },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
                            },
                            addNewItem: { 
                                method: 'modal-form', 
                                modalForm: <SupplierForm />, 
                                linkedItem: { contact_type: 'supplier', entity_type: 'organization' }
                            }
                        },
                    ]
                },
                {
                    title: 'purchase.general.client_card_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'order',
                            label: 'purchase.general.order_label',
                            placeholder: 'purchase.general.order_placeholder',
                            objectName: 'order.general.object_name.singular',
                            query: { endpoint: 'get_order_search' },
                            activeFeature: 'orders',
                            disableAddNew: true,
                            selectionFormat: 'number',
                            optionFormat: { 
                                title: { field: 'number', format: (value) => value } ,
                                subtitle: { field: 'client_name', format: (value) => value.client_name, optional: true},
                            },
                        },
                        { 
                            type: 'searchselect', 
                            name: 'job',
                            label: 'purchase.general.job_label',
                            placeholder: 'purchase.general.job_placeholder',
                            objectName: 'job.general.object_name.singular',
                            query: { endpoint: 'get_job_search' },
                            activeFeature: 'jobs',
                            disableAddNew: true,
                            selectionFormat: 'number',
                            optionFormat: { title: { field: 'number', format: (value) => value } },
                        },
                    ],
                },
            ]
        },
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'purchase.general.details_card_header',
                    fields: [
                        { 
                            type: 'reference', 
                            name: 'reference',
                            label: 'purchase.general.reference_label',
                        },
                        { 
                            type: 'dropdown', 
                            name: 'division', 
                            label: 'job.general.division_label', 
                            apiRequest: [{
                                endpoint: 'get_division_list',
                                object: 'division',
                                params: { is_active: true },
                                useCache: true
                            }],
                            activeFeature: 'divisions',
                            disabled_selected: 'job.general.division_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'dropdown', 
                            name: 'managed_by_user', 
                            label: 'purchase.general.managed_by_user_label', 
                            apiRequest: [{
                                endpoint: 'get_user_list',
                                object: 'office_user',
                                params: { is_active: true, office_access: true },
                                useCache: true
                            }],
                            disabled_selected: 'purchase.general.managed_by_user_disabled_selected',
                            optionFormat: 'full_name',
                            selectionFormat: 'full_name',
                            allowNoneOption: false,
                            showSearch: false,
                        },     
                    ]
                },
                {
                    fields: [
                        {
                            type: 'date',
                            name: 'purchase_date',
                            label: 'purchase.general.purchase_date_label', 
                            tooltipText: 'purchase.general.purchase_date_tooltipText',
                            isEditable: { editability: false },
                        },
                        { 
                            type: 'dropdown', 
                            name: 'is_checked', 
                            label: 'purchase.general.is_checked_label', 
                            disabled_selected: 'forms.select_type_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                            hardcodedOptions: [
                                { value: true, name: 'forms.true_value'},
                                { value: false, name: 'forms.false_value'},
                            ],
                            tooltipText: 'purchase.general.is_checked_tooltipText',
                            dataCondition: [{ field: 'status', notValues: ['draft'] }],
                        },
                        {
                            type: 'date',
                            name: 'expected_delivery_date',
                            label: 'purchase.general.expected_delivery_date_label', 
                            dataCondition: [{ field: 'status', notValues: ['delivered'] }],
                        },
                        {
                            type: 'date',
                            name: 'expected_delivery_date',
                            label: 'purchase.general.delivery_date_label', 
                            dataCondition: [{ field: 'status', values: ['delivered'] }],
                        },
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'purchase.general.description_header',
            alignment: 'stacked',
            fields: [
                { 
                    type: 'textarea', 
                    name: 'description', 
                    emptyLabel: 'purchase.general.description_emptyLabel',
                    placeholder: 'purchase.general.description_placeholder', 
                    rows: 5,
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'purchase.general.attachments_header',
            fields: [
                {
                    type: 'attachments',
                    name: 'attachments',
                    apiRequest: [{ 
                        endpoint: 'get_attachmenttype_list', 
                        object: 'attachmenttype', 
                        params: { 'deleted': false }
                    }]
                }
            ]
        },
    ]
    
    return (
        <DetailModal 
            viewKey={viewKey}
            objectType={object}
            apiObject={apiObject}
            itemId={itemId}
            headerButtons={headerButtons}
            statusBar={showStatusBar}
            sections={sections}
            summary={showSummary}
        />
    );
};

export default PurchaseModal;